<template>
  <v-row v-if="!isResellerSubAccount" v-bind="$attrs">
    <v-col cols="12">
      <gg-card>
        <gg-card-text>
          <div class="body-1 mb-5">
            <v-icon size="24" color="primary" class="pb-1">mdi-account-settings</v-icon>
            <span class="ml-2 font-weight-medium">{{ serviceInfoTitle }}</span>
          </div>
          <v-row>
            <v-col cols="6" class="pl-4 pr-9">
              <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 6px">
                <div style="flex: 0 0 45%;" class="font-weight-bold">Plan Type:</div>
                <div style="flex: 0 0 45%; text-align: right">
                  <span>
                    {{ hosting.name }}
                  </span>
                  <v-icon size="medium">
                    {{ iconFlag(hosting.flag) }}
                  </v-icon>
                  <a v-if="hosting.changeplan"
                     href="#"
                     class="ml-5"
                     @click.prevent="handleChangePlan">
                    Change Plan
                  </a>
                </div>
                <div style="flex: 0 0 45%;" class="font-weight-bold">Status:</div>
                <div style="flex: 0 0 45%; text-align: right">
                  <v-icon small class="pb-1" :color="statusColor(hosting.status)">mdi-circle</v-icon>
                  <span>
                    {{ hosting.status }}
                  </span>
                </div>
                <div style="flex: 0 0 45%;" class="font-weight-bold">Billing Cycle:</div>
                <div style="flex: 0 0 45%; text-align: right">
                  {{ hosting.renewalterm }}
                </div>
                <div style="flex: 0 0 45%;" class="font-weight-bold">Renewal Date:</div>
                <div style="flex: 0 0 45%; gap: 1rem;" class="d-flex justify-end align-center text-right">
                  <span>{{ formatRenewalDate(parseRenewalDate(hosting.renewaldate)) }}</span>
                  <a
                    v-if="currentHosting.can_access_early_renewal"
                    href="#"
                    @click.prevent="handleRenewNow"
                  >
                    Renew Now
                  </a>
                </div>
              </div>
            </v-col>
            <v-col cols="6" class="pl-9 pr-4">
              <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 6px">
                <div style="flex: 0 0 45%;" class="font-weight-bold">Password Management:</div>
                <div style="flex: 0 0 45%; text-align: right">
                  <a href="#" @click="openChangePasswordModal($event, hosting_detail)">
                    Change Password
                  </a>
                </div>
                <div style="flex: 0 0 45%;" class="font-weight-bold">Sharing Options:</div>
                <div style="flex: 0 0 45%; text-align: right">
                  <shared-service-tag v-if="hosting.sharedaccount || hosting.sharedaccess"
                                      :service="hosting"></shared-service-tag>
                  <router-link v-else :to="{ name: 'profile_access' }">
                    Enable Sharing
                  </router-link>
                </div>
                <template v-if="currentHosting.cancellation_type === 'addon'">
                  <div style="flex: 0 0 45%;" class="font-weight-bold">Addon Cancellation:</div>
                  <div style="flex: 0 0 45%; text-align: right">
                    <router-link
                      :to="`/cancellations/${currentHosting.cancellation_id}`"
                      aria-label="View Request">
                      View Request
                    </router-link>
                  </div>
                </template>
                <div style="flex: 0 0 45%;" class="font-weight-bold">Auto Renew:</div>
                <div style="flex: 0 0 45%; text-align: right">
                  <div v-if="currentHosting.autorenew">
                    <v-icon small class="pb-1" :color="statusColor(1)">mdi-circle</v-icon>
                    <span class="pl-1">Enabled</span>
                    <router-link
                      v-if="currentHosting.autorenew"
                      class="ml-4"
                      :to="{ name: 'cancel', params: { service_id: hosting.id }, query: { type: cancellationTypes.HOSTING }}">
                      {{ hosting.status === 'Suspended' ? 'Submit Cancellation' : 'Disable' }}
                    </router-link>
                  </div>
                  <div v-else>
                    <v-icon small class="pb-1" :color="statusColor(0)">mdi-circle</v-icon>
                    <span class="font-bold pl-1">Disabled</span>
                    <a href="#" @click="canAutorenewProceed">
                      Enable
                    </a>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
          <gg-modal :is-active="autorenewModalOpen" @close="closeModalAutorenew">
            <template v-slot:toolbar>
              Enabling auto renew error
            </template>
            <template v-slot:body>
              Your service is not eligible to enable auto renew. Please contact Billing for assistance.
            </template>
            <template v-slot:actions>
              <gg-btn @click="closeModalAutorenew">Close</gg-btn>
            </template>
          </gg-modal>
          <gg-modal :is-active="autorenewModalConfirmOpen" @close="closeModalAutorenewConfirm">
            <template v-slot:toolbar>
              Enabling auto renew
            </template>
            <template v-slot:body>
              <p>This service will be set to auto-renew. Our system will automatically renew your account to ensure your account stays active.</p>

              <p>Are you sure you want to enable auto renewal?</p>
            </template>
            <template v-slot:actions>
              <gg-btn secondary @click="closeModalAutorenewConfirm">No</gg-btn>
              <gg-btn primary :loading="autorenewLoading" @click="acceptModalAutorenewConfirm">Yes</gg-btn>
            </template>
          </gg-modal>
        </gg-card-text>
      </gg-card>
      <gg-modal :is-active="modalChangePassword" @close="closeChangePasswordModal">
        <template v-slot:toolbar>
          Change Password
        </template>
        <template v-slot:body>
          <compact-password
            outlined
            cpanel-app="passwd"
            v-model="password"
            :label="`New Password for ${user}`"
            show-strength
            with-generator
            charset="abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMOPRSTUVWXYZ0123456789_"
            autocomplete="new-password"
            v-validate="modalChangePassword ? 'required|min:8' : ''"
            data-vv-name="password"
            data-vv-as="Password"
            :error-messages="errors.collect('password')"
          />
        </template>
        <template v-slot:actions>
          <gg-btn secondary @click="closeChangePasswordModal">
            Cancel
          </gg-btn>
          <gg-btn primary @click="updatePassword" :loading="isUpdatingPassword">
            Change
          </gg-btn>
        </template>
      </gg-modal>
      <renew-now-modal ref="renewNowModal" :preferred-term="renewNowTerm"></renew-now-modal>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  statusColor,
  parseRenewalDate,
  iconFlag,
  formatRenewalDate
} from '@/helpers/helpers'
import SharedServiceTag from 'Components/Common/SharedServiceTag'
import { cancellationTypes } from '@/consts/cancellations'
import CompactPassword from '@/components/Common/password/CompactPassword'
import RenewNowModal from 'Components/Services/Hosting/Shared/Overview/RenewNowModal'

export default {
  name: 'ServiceDetails',
  components: {
    SharedServiceTag,
    CompactPassword,
    RenewNowModal,
  },
  data () {
    return {
      autorenewModalOpen: false,
      cancellationTypes,
      autorenewModalConfirmOpen: false,
      autorenewLoading: false,
      serviceInfoTitle: '',
      modalChangePassword: false,
      user: '',
      isUpdatingPassword: false,
      password: '',
    }
  },
  computed: {
    ...mapGetters([
      'hosting',
      'services',
      'hosting_detail',
    ]),
    currentHosting () {
      return this.services.find(service => service.id == this.hosting.id)
    },
    isResellerSubAccount () {
      return this.$route.query.user && this.hosting.groupname === 'Reseller Hosting'
    },
    renewNowTerm () {
      // Check if 'renewnow' key exists in query params
      if ('renewnow' in this.$route.query) {
        // Return the value if provided, otherwise default to 'annually'
        return this.$route.query.renewnow || 'annually'
      }
      return null
    },
  },
  methods: {
    ...mapActions([
      'stopRenewal',
      'enableAutorenew',
      'setHostingPassword',
      'reportError',
      'reportSuccess',
    ]),
    statusColor,
    parseRenewalDate,
    iconFlag,
    formatRenewalDate,
    handleChangePlan () {
      if (this.hosting.updowngrade_request === 0) {
        this.$router.push({
          name: 'hosting_create_request',
          query: {
            user: this.$route.query.user
          },
        })
      } else {
        this.$router.push({
          name: 'hosting_view_request',
          params: {
            id: this.hosting.updowngrade_request
          },
          query: {
            user: this.$route.query.user
          },
        })
      }
    },
    closeModalAutorenew () {
      this.autorenewModalOpen = false
    },
    openModalAutorenew () {
      this.autorenewModalOpen = true
    },
    canAutorenewProceed () {
      if (!this.hosting.can_enable_autorenew) {
        this.openModalAutorenew()
        return
      }
      this.openModalAutorenewConfirm()
    },
    async callEnableAutorenew () {
      await this.enableAutorenew({ service_id: this.hosting.id, service_type: cancellationTypes.HOSTING, cancellation_id: this.hosting.cancellation_id })
    },
    closeModalAutorenewConfirm () {
      this.autorenewModalConfirmOpen = false
    },
    openModalAutorenewConfirm () {
      this.autorenewModalConfirmOpen = true
    },
    async acceptModalAutorenewConfirm () {
      this.autorenewLoading = true
      try {
        await this.callEnableAutorenew()
      } finally {
        this.autorenewLoading = false
      }
      this.closeModalAutorenewConfirm()
    },
    setServiceInfoTitle () {
      this.serviceInfoTitle = this.$route.name === 'hosting_whm_info' ? 'Reseller Account Details' : 'Hosting Account Details'
    },
    openChangePasswordModal (event, serverInfo) {
      event.preventDefault()
      this.modalChangePassword = true
      this.user = serverInfo.user ? serverInfo.user : serverInfo.accountsummary.user
    },
    closeChangePasswordModal () {
      this.modalChangePassword = false
    },
    reset () {
      this.password = ''
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },
    async updatePassword () {
      const valid = await this.$validator.validateAll()
      if (!valid) {
        return
      }
      this.isUpdatingPassword = true
      const data = {
        pass: this.password,
        username: this.user,
      }
      try {
        const message = await this.setHostingPassword(data)
        this.reportSuccess(message)
      } catch (e) {
        this.reportError(e)
      }
      this.isUpdatingPassword = false
      this.modalChangePassword = false
      this.reset()
    },
    handleRenewNow () {
      this.$refs.renewNowModal.showModal()
    },
    checkRenewNowQuery () {
      if (this.renewNowTerm && this.currentHosting?.can_access_early_renewal) {
        this.$nextTick(() => {
          this.$refs.renewNowModal.showModal()
        })
      }
    },
  },
  watch: {
    hosting: {
      handler (newVal) {
        if (newVal && newVal.id) {
          this.checkRenewNowQuery()
        }
      },
      immediate: true,
    },
  },
  created () {
    this.setServiceInfoTitle()
  }
}
</script>
